import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown } from 'react-bootstrap';
import { isValid, isWithinInterval, parseISO } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { Settings } from '../../models/settings.model';
import { Tax } from '../../models/tax.model';
import { Names } from '../../models/names.model';
import { legendValid } from '../../utils/legend.data.util';
import { suffixLegend } from '../../scripts/name.legend.script';
import { formatTaxRate } from '../../scripts/format.value.script';
import { taxPurchaseCodes, taxSaleCodes } from '../../libraries/tax.code.library';
import { calculateRebate } from '../../scripts/financial.calculation.script';

import AppLegend from './Legend';

export interface AppInputRequirementTaxProps {
  companyForUser: Company,
  settingsForCompany: Settings,
  type: 'sale' | 'purchase',
  date: string,
  content: {value: any, index: number, sub_index: number},
  requirements: {list: boolean, value: {type: string, description: string, contents: any[]}[], valid: {type: boolean, description: boolean, content: boolean, contents: any[]}[]},
  setRequirements: Dispatch<SetStateAction<{list: boolean, value: {type: string, description: string, contents: any[]}[], valid: {type: boolean, description: boolean, content: boolean, contents: any[]}[]}>>,
  validateRebaja?: Function
};

const AppInputRequirementTax: React.FunctionComponent<AppInputRequirementTaxProps> = ({companyForUser, settingsForCompany, type, date, content, requirements, setRequirements, validateRebaja}) => {
  const {lang} = StateLanguage()

  const handleChangeTaxRateContentToRequirement = (item: Tax) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (content.index === temp_index) {
        temp_item.contents[content.sub_index].tax_code = item.code
        temp_item.contents[content.sub_index].tax_rate = item.rate
        if (validateRebaja) {
          temp_item.contents[content.sub_index].rebate_amount = calculateRebate(temp_item.contents[content.sub_index].reference, temp_item.contents[content.sub_index].quantity, temp_item.contents[content.sub_index].price_unit, temp_item.contents[content.sub_index].tax_included, item.rate, temp_item.contents[content.sub_index].rebate_type, temp_item.contents[content.sub_index].rebate_value, temp_item.contents[content.sub_index].rebate_quantity, temp_item.contents[content.sub_index].rebate_amount)
        }
      }
      return temp_item
    })
    let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
      if (content.index === temp_index) {
        temp_item.contents[content.sub_index].tax = true
      }
      return temp_item
    })

    if (validateRebaja) {
      validateRebaja(content.index, content.sub_index)
    }

    setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})
    legendValid(`legend-requirement-content-tax-required${suffixLegend(content.index, content.sub_index)}`)
  }

  function taxFees(): Tax[] {
    const safeDate = date && date.length > 0 ? date : new Date().toISOString().slice(0, 10)
    let isValidDate: boolean = isValid(parseISO(safeDate))

    switch (type) {
      case 'sale':
        return settingsForCompany?.tax_fees?.filter(item => (settingsForCompany?.subject_tax ? item.code !== 'NO_VAT' : item.behavior === 'SALE_ZERO_VAT') && taxSaleCodes().includes(item.code) && item.active && isValidDate && (!item.effectiveFrom || !item.effectiveTo || isWithinInterval(parseISO(safeDate), { start: parseISO(item.effectiveFrom), end: parseISO(item.effectiveTo) }))) || []
      case 'purchase':
        return settingsForCompany?.tax_fees?.filter(item => (settingsForCompany?.subject_tax ? item.behavior !== '' : item.behavior === 'PURCHASE_NON_DEDUCTIBLE') && taxPurchaseCodes().includes(item.code) && item.active && isValidDate && (!item.effectiveFrom || !item.effectiveTo || isWithinInterval(parseISO(safeDate), { start: parseISO(item.effectiveFrom), end: parseISO(item.effectiveTo) }))) || []
      default:
        return []
    }
  }

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
          <span className="selection">
            <span className="select2-selection select2-selection--single form-select bg-light px-2" style={{backgroundImage: requirements.valid[content.index].contents[content.sub_index].tax_rate ? "none" : ""}}>
              <span className="select2-selection__rendered d-flex align-items-center" role="textbox">
                { requirements.valid[content.index].contents[content.sub_index].tax
                  ?
                  <>
                    {formatTaxRate(content.value.tax_rate)}
                    <div className="fw-bolder fs-9 ms-1">
                      {"["}{content.value.tax_code}{"]"}
                    </div>
                  </>
                  :
                  <>
                    {"\u00A0"}
                  </>
                }
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" role="listbox">
                <li className={`select2-results__option select2-results__option--selectable ${(settingsForCompany?.tax_fees ?? []).length > 0 && "d-none"}`}>
                  <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                </li>
                { taxFees().map (( (item, index) => { return (
                  <li key={index} className={`select2-results__option select2-results__option--selectable ${item.code === content.value.tax_code && "select2-results__option--selected"}`} role="option" aria-selected={item.code === content.value.tax_code}>
                    <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxRateContentToRequirement(item)}>{item.label[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</Dropdown.Item>
                  </li>
                )}))}
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      <AppLegend attribute={{check: requirements.valid[content.index].contents[content.sub_index].tax, name: "requirement-content-tax", index: content.index, sub_index: content.sub_index}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
    </>
  )
}

export default AppInputRequirementTax;
